// Airport code to full name mapping
// TravelSki origins/destinations kept in sync with
// booking-funnel-xftless/backend/flight-connector/src/utils/locationMapping.ts
export const AIRPORT_CODES: Record<string, string> = {
  // United Kingdom - Major Airports
  'ABZ': 'Aberdeen',
  'ACI': 'Alderney',
  'BFS': 'Belfast International',
  'BHD': 'George Best Belfast City',
  'BHX': 'Birmingham',
  'BOH': 'Bournemouth',
  'BRS': 'Bristol',
  'CWL': 'Cardiff',
  'CEG': 'Chester Hawarden',
  'LDY': 'City of Derry',
  'CAX': 'Carlisle Lake District',
  'DSA': 'Doncaster Sheffield',
  'DND': 'Dundee',
  'EMA': 'East Midlands',
  'EDI': 'Edinburgh',
  'EXT': 'Exeter',
  'GCI': 'Guernsey',
  'GLA': 'Glasgow',
  'PIK': 'Glasgow Prestwick',
  'HUY': 'Humberside',
  'INV': 'Inverness',
  'IOM': 'Isle of Man',
  'JER': 'Jersey',
  'LBA': 'Leeds Bradford',
  'LPL': 'Liverpool John Lennon',
  'LCY': 'London City',
  'LGW': 'London Gatwick',
  'LHR': 'London Heathrow',
  'LTN': 'London Luton',
  'SEN': 'London Southend',
  'STN': 'London Stansted',
  'MAN': 'Manchester',
  'NCL': 'Newcastle International',
  'NQY': 'Newquay',
  'NWI': 'Norwich',
  'SOU': 'Southampton',
  'MME': 'Teesside International',

  // France - Alpine Airports
  'LYS': 'Lyon-Saint-Exupéry',
  'GNB': 'Grenoble Alpes Isère',
  'CMF': 'Chambéry-Savoie',
  'MFL': 'Mount Full Stop Airport',
  'NCE': "Nice Côte d'Azur",

  // Italy - Alpine Airports
  'TRN': 'Turin-Caselle',

  // Austria / Germany - Alpine Airports
  'MUC': 'Munich',
  'INN': 'Innsbruck',
  'SZG': 'Salzburg',

  // Switzerland - Alpine Airports
  'GVA': 'Genève-Cointrin',
  'ZRH': 'Zurich',
  'BSL': 'Basel',
  'BRN': 'Bern',

  // Ireland
  'DUB': 'Dublin',
  'SNN': 'Shannon',
  'ORK': 'Cork',
  'NOC': 'Ireland West Knock',

  // Other major European airports (display helpers)
  'CDG': 'Paris Charles de Gaulle',
  'ORY': 'Paris Orly',
  'AMS': 'Amsterdam',
  'FRA': 'Frankfurt',
  'MAD': 'Madrid',
  'BCN': 'Barcelona',
  'FCO': 'Rome',
  'MXP': 'Milan Malpensa',
  'VIE': 'Vienna',
  'CPH': 'Copenhagen',
  'ARN': 'Stockholm',
  'OSL': 'Oslo',
  'HEL': 'Helsinki',
}

// Train station code to full name mapping
// Kept in sync with booking-funnel locationMapping.ts
export const TRAIN_STATION_CODES: Record<string, string> = {
  'STP': 'Gare London St Pancras',
  'XAP': 'Gare Aime La Plagne',
  'XBM': 'Gare Bourg St Maurice',
  'XAV': 'Gare Albertville',
  'XMO': 'Gare Moutiers',
  'XDB': 'Gare Lille Europe',
  'PLY': 'Gare Paris Gare de Lyon',
  'PAZ': 'Gare Paris Austerlitz',

  /** TNE Benelux hubs */
  'AMS': 'Gare Amsterdam Centraal',
  'RTM': 'Gare Rotterdam Centraal',
  'ANR': 'Gare Anvers',
  'BRU': 'Gare Bruxelles',
}

// Train company code to full name mapping
export const TRAIN_COMPANY_CODES: Record<string, string> = {
  '9F': 'Eurostar',
}

/**
 * Get the full airport name from airport code
 * @param code - Airport IATA code (e.g., 'LHR')
 * @returns Full airport name or the original code if not found
 */
export function getAirportName(code: string): string {
  if (!code) return code
  return AIRPORT_CODES[code.toUpperCase()] || code
}

/**
 * Get the full train station name from station code
 * @param code - Train station code (e.g., 'STP')
 * @returns Full station name or the original code if not found
 */
export function getTrainStationName(code: string): string {
  if (!code) return code
  return TRAIN_STATION_CODES[code.toUpperCase()] || code
}

/**
 * Get the full train company name from company code
 * @param code - Train company code (e.g., '9F')
 * @returns Full company name or the original code if not found
 */
export function getTrainCompanyName(code: string): string {
  if (!code) return code
  return TRAIN_COMPANY_CODES[code.toUpperCase()] || code
}

/**
 * Format airport display with both code and name
 * @param code - Airport IATA code
 * @returns Formatted string like "London Heathrow (LHR)" or just the code if not found
 */
export function formatAirportDisplay(code: string): string {
  if (!code) return code
  const upper = code.toUpperCase()
  const name = AIRPORT_CODES[upper]
  return name ? `${name} (${upper})` : code
}

/**
 * Format train station display with both code and name
 * @param code - Train station code
 * @returns Formatted string like "London St Pancras International (STP)" or just the code if not found
 */
export function formatTrainStationDisplay(code: string): string {
  if (!code) return code
  const upper = code.toUpperCase()
  const name = TRAIN_STATION_CODES[upper]
  return name ? `${name} (${upper})` : code
}

/**
 * Format train company display with both code and name
 * @param code - Train company code
 * @returns Formatted string like "First TransPennine Express (9F)" or just the code if not found
 */
export function formatTrainCompanyDisplay(code: string): string {
  if (!code) return code
  const upper = code.toUpperCase()
  const name = TRAIN_COMPANY_CODES[upper]
  return name ? `${name} (${upper})` : code
}
